import dynamic from 'next/dynamic';
import styled from 'styled-components';

import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { FaBars } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { APP_IMG, RECAPTCHA_SITE_KEY, SHOW_COMMEMORATE } from 'src/constants';
import { WEB_NAME, WEB_TITLE } from 'src/constants/layout';
import { SHOW_MEMBER } from 'src/services/member-auth/constants';
import { DATALAYER_TYPE, sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';
import useResponsive from 'src/utils/device';
import { convertObjPathNoneEncode } from 'src/utils/helper';

const NavDesktop = dynamic(import('src/components/layouts/nav-desktop'));
const NavMobile = dynamic(import('src/components/layouts/nav-mobile'));
const WidgetLogin = dynamic(import('src/services/member-auth/components/widget-login'));
const MenuNavbar = dynamic(import('src/components/widgets/menu-navbar'));

type Props = {
  data: any;
  sessionSevId: any;
  isOverlay?: boolean;
  isHomePage?: boolean;
};

const Header = ({ data, sessionSevId, isOverlay = false, isHomePage = true }: Props) => {
  const router = useRouter();
  const [toggle, setToggle] = useState(false);
  const [expand, setExpand] = useState({ status: false, key: null });
  const responsive = useResponsive();
  // ========== Search Logic ==========
  const recaptchaRef = useRef<any>(null);
  const [recaptchaBox, setRecaptchaBox] = useState(false);

  const { register, handleSubmit, watch } = useForm();

  const [textSearch, setTextSearch] = useState('');

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!textSearch?.trim()) return;
    setRecaptchaBox(true);
  };

  const onReCAPTCHAChange = async (captchaCode: any) => {
    if (!captchaCode) return null;

    try {
      const response = await fetch('/api/recaptcha', {
        method: 'POST',
        body: JSON.stringify({ textSearch, captcha: captchaCode }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setRecaptchaBox(false);
        const searchParams = convertObjPathNoneEncode({
          q: encodeURIComponent(textSearch || '')
        });
        window.location.href = `/search${searchParams}`;
      } else {
        const error = await response.json();
        throw new Error(error.message);
      }
    } catch (err: any) {
      alert(err?.message || 'Something went wrong');
      setRecaptchaBox(false);
    } finally {
      recaptchaRef.current = null;
      setRecaptchaBox(false);
    }
  };

  function handleToggle(_key: any, _index: any) {
    switch (_key) {
      case 'close':
        setToggle(false);
        setExpand({ key: null, status: false });
        break;
      case 'open':
        setToggle(!toggle);
        break;
      case 'menu':
        setToggle(true);
        setExpand({
          key: _index,
          status: expand?.key === _index ? !expand.status : true
        });
        break;
      default:
        setToggle(false);
        break;
    }
  }

  const isDesktop = !responsive.isMobile && !responsive.isTablet;
  const isTabletOrMobile = responsive.isMobile || responsive.isTablet;

  return (
    <>
      {isDesktop && toggle && <NavDesktop sessionSevId={sessionSevId} data={data} handleToggle={handleToggle} />}

      {/* ReCAPTCHA Overlay */}
      {recaptchaBox && (
        <RecaptchaOverlay>
          <div className='recaptcha-backdrop' onClick={() => setRecaptchaBox(false)} />
          <div className='recaptcha-container'>
            <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} onChange={onReCAPTCHAChange} />
          </div>
        </RecaptchaOverlay>
      )}

      <HeaderWrapper className={isOverlay ? 'header-overlay' : 'header-normal'}>
        <div className='nav-container'>
          {/* Desktop Layout */}
          <div className='desktop-layout'>
            {/* Logo - Left */}
            <div className='header-logo' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
              <a
                href='/'
                title={WEB_TITLE}
                onClick={() =>
                  sendDataLayerGlobal({
                    type: DATALAYER_TYPE.TRACK,
                    router: router?.pathname,
                    section: 'header',
                    data: { title: 'logo', heading: 'logo' }
                  })
                }
              >
                <img src={`${APP_IMG}/logo-white.webp`} alt={WEB_NAME} width={212} height={60} />
              </a>
            </div>

            {/* Search Box - Center */}
            <div className='header-search' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
              <form className='search-box' onSubmit={onSearch}>
                <input type='text' placeholder='คุณสามารถค้นหาคนที่คุณสนใจได้ที่นี่....' value={textSearch} onChange={e => setTextSearch(e.target.value)} />
                <button type='submit' className='search-btn' aria-label='Search'>
                  <FiSearch />
                </button>
              </form>
              <div className='tablet-layout' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                <MenuNavbar
                  _data={data}
                  eventDataLayer={{
                    type: DATALAYER_TYPE.TRACK_POSITION,
                    router: router?.pathname,
                    section: 'section-1',
                    position: 'ใต้:header'
                  }}
                />
              </div>
            </div>

            {/* Icons - Right */}
            <div className='header-icons'>
              <button className='icon-btn' aria-label='Bars' onClick={() => handleToggle('open', null)}>
                <FaBars />
              </button>
              {SHOW_MEMBER === 'true' && <WidgetLogin sessionSevId={sessionSevId} />}
            </div>
          </div>

          {/* Mobile/Tablet Layout */}
          <div className='mobile-layout'>
            <div className='mobile-header'>
              {/* Left - NavMobile */}
              <div className='mobile-left' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                <NavMobile data={data} />
              </div>

              {/* Logo - Center */}
              <div className='mobile-logo' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                <a
                  href='/'
                  title={WEB_TITLE}
                  onClick={() =>
                    sendDataLayerGlobal({
                      type: DATALAYER_TYPE.TRACK,
                      router: router?.pathname,
                      section: 'header',
                      data: { title: 'logo', heading: 'logo' }
                    })
                  }
                >
                  {isHomePage ? <img src={`${APP_IMG}/logo-white.webp`} alt={WEB_NAME} /> : <img src={`${APP_IMG}/logo-white-mobile.png`} alt={WEB_NAME} />}
                </a>
              </div>

              {/* Right - Icons */}
              <div className='mobile-icons'>{SHOW_MEMBER === 'true' && <WidgetLogin sessionSevId={sessionSevId} />}</div>
            </div>

            {/* Search Box - Always Visible */}
            <form className='mobile-search-box' onSubmit={onSearch}>
              <div className='search-input-wrapper'>
                <input type='text' placeholder='คุณสามารถค้นหาคนที่คุณสนใจได้ที่นี่....' value={textSearch} onChange={e => setTextSearch(e.target.value)} />
                <button type='submit' className='mobile-search-btn' aria-label='Search'>
                  <FiSearch />
                </button>
              </div>
            </form>
          </div>
        </div>
      </HeaderWrapper>
    </>
  );
};

const HeaderWrapper = styled.header`
  position: sticky;
  top: 0;
  z-index: 15;
  transition: all 0.3s ease;

  input::placeholder {
    font-style: italic;
    font-family: 'Prompt';
    font-weight: 300;
  }

  &.header-normal {
    background-color: #000000ed !important;
    .expand-more-menu {
      background-color: #000000ed !important;
      box-shadow: 0px 5px 6px -6px rgba(0, 0, 0, 0.3);
    }
  }

  &.header-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: transparent;
  }

  .nav-container {
    max-width: 2000px;
    margin: 0 auto;
    padding: 20px;

    @media (max-width: 1024px) {
      padding: 15px;
    }
  }

  /* ==================== Desktop Layout ==================== */
  .desktop-layout {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 60px;

    @media (max-width: 1024px) {
      display: none;
    }
  }

  .header-logo {
    flex: 0 0 auto;

    img {
      height: 60px;
      width: 212px;

      @media (max-width: 1200px) {
        height: 50px;
      }
    }
  }

  .header-search {
    flex: 1;

    .search-box {
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.263);
      border-radius: 25px;
      overflow: hidden;

      input {
        flex: 1;
        background: transparent;
        border: none;
        padding: 12px 20px;
        color: #fff;
        font-size: 16px;
        outline: none;

        &::placeholder {
          color: rgba(255, 255, 255, 0.6);
        }

        @media (max-width: 1200px) {
          padding: 10px 15px;
          font-size: 14px;
        }
      }

      .search-btn {
        background: transparent;
        border: none;
        padding: 8px 15px;
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;

        svg {
          font-size: 24px;

          @media (max-width: 1200px) {
            font-size: 20px;
          }
        }
      }
    }
  }

  .header-icons {
    display: flex;
    align-items: center;
    gap: 20px;

    .icon-btn {
      background: transparent;
      border: none;
      color: #fff;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;

      svg {
        font-size: 34px;

        @media (max-width: 1200px) {
          font-size: 28px;
        }
      }

      &:hover {
        opacity: 0.8;
      }
    }
  }

  .tablet-layout {
    display: block;
    @media (max-width: 1280px) {
      display: none;
    }
  }
  /* ==================== Mobile/Tablet Layout ==================== */
  .mobile-layout {
    display: none;

    @media (max-width: 1024px) {
      display: block;
    }
  }

  .mobile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 15px;

    @media (max-width: 480px) {
      gap: 20px;
    }
  }

  .mobile-left {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .mobile-logo {
    flex: 0 0 auto;
    text-align: center;

    img {
      height: 55px;
      width: auto;

      @media (max-width: 768px) {
        height: 55px;
      }
    }
  }

  .mobile-icons {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;

    .icon-btn {
      background: transparent;
      border: none;
      color: #fff;
      cursor: pointer;
      padding: 5px;

      svg {
        font-size: 20px;

        @media (max-width: 480px) {
          font-size: 18px;
        }
      }
    }
  }

  .mobile-search-box {
    width: 100%;
    padding-top: 10px;

    @media (max-width: 480px) {
      padding-top: 8px;
    }

    .search-input-wrapper {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;

      input {
        width: 100%;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 20px;
        padding: 10px 40px 10px 15px;
        color: #fff;
        font-size: 14px;
        outline: none;

        @media (max-width: 480px) {
          padding: 8px 35px 8px 12px;
          font-size: 12px;
          border-radius: 15px;
        }

        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
      }

      .mobile-search-btn {
        position: absolute;
        right: 5px;
        background: transparent;
        border: none;
        color: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        padding: 8px;

        @media (max-width: 480px) {
          padding: 6px;
          right: 3px;
        }

        svg {
          font-size: 18px;

          @media (max-width: 480px) {
            font-size: 16px;
          }
        }

        &:hover {
          color: #fff;
        }
      }
    }
  }
`;

const RecaptchaOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;

  .recaptcha-backdrop {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.7);
  }

  .recaptcha-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export default Header;
